import { useState, type ChangeEvent } from 'react';
import { Controller, useWatch, type Control, type FieldPath, type UseFormSetValue } from 'react-hook-form';
import { t, tn } from '@app/i18n';
import { dataCurta } from '@app/format';
import { Alert, Button, Card, Field, FieldGrid, IconButton, TextArea, TextInput, type FieldSpan } from '@zarya/ui';
import type { AdmissionSettings, SiteAdmission } from './SiteAdmissionModel';
import s from './SiteAdmission.module.css';

type Name = FieldPath<AdmissionSettings>;

interface SettingFieldProps {
  control: Control<AdmissionSettings>;
  name: Name;
  label: string;
  span?: FieldSpan;
  kind?: 'text' | 'number';
  required?: boolean;
  tip?: string;
  placeholder?: string;
  mono?: boolean;
  disabled?: boolean;
}

/**
 * Um campo do formulário. O número vai como número para o contrato (`integer`): o campo vazio vira `NaN`, que a
 * validação recusa antes do envio, e nunca um `0` inventado que o servidor leria como porta.
 */
function SettingField({ control, name, label, span = 6, kind = 'text', required, tip, placeholder, mono = true, disabled }: SettingFieldProps) {
  return (
    <Controller
      control={control}
      name={name}
      render={({ field, fieldState }) => (
        <Field label={label} span={span} required={required} tip={tip} error={fieldState.error?.message}>
          {(props) => (
            <TextInput
              {...props}
              name={field.name}
              ref={field.ref}
              onBlur={field.onBlur}
              mono={mono}
              disabled={disabled}
              autoComplete="off"
              inputMode={kind === 'number' ? 'numeric' : undefined}
              placeholder={placeholder}
              value={field.value === undefined || (typeof field.value === 'number' && Number.isNaN(field.value)) ? '' : String(field.value)}
              onChange={(event: ChangeEvent<HTMLInputElement>) => {
                const raw = event.target.value;
                field.onChange(kind === 'number' ? (raw.trim() === '' ? Number.NaN : Number(raw)) : raw);
              }}
            />
          )}
        </Field>
      )}
    />
  );
}

export interface AdmissionSettingsFormProps {
  control: Control<AdmissionSettings>;
  setValue: UseFormSetValue<AdmissionSettings>;
  page: SiteAdmission;
}

/**
 * As seções editáveis do protótipo §2: Gateway, Troca de token da pessoa (STS), Assinatura de imagem e o
 * «Avançado» recolhido. Identidade e Releases aprovadas são só leitura e ficam na página.
 */
export function AdmissionSettingsForm({ control, setValue, page }: AdmissionSettingsFormProps) {
  // J7: sem registry do produto na instalação, os padrões vêm sem imagem dos sidecars, e a seção abre sozinha —
  // senão o campo obrigatório ficaria escondido atrás do «Avançado» recolhido.
  const imagesMissing = !page.settings.advanced.helper.image || !page.settings.advanced.proxy.image;
  const [advanced, setAdvanced] = useState(imagesMissing);
  const stsEnabled = useWatch({ control, name: 'sts.enabled' });
  const enforce = useWatch({ control, name: 'signatures.enforce' });
  const globs = useWatch({ control, name: 'signatures.image_globs' }) ?? [];
  const certificates = useWatch({ control, name: 'signatures.certificates' }) ?? [];
  const certificateList = page.latest?.signature_certificates ?? [];
  const gatewayCA = useWatch({ control, name: 'gateway.trusted_ca_bundle' });
  const [glob, setGlob] = useState('');
  const [pem, setPem] = useState('');

  function setList(name: 'signatures.image_globs' | 'signatures.certificates', values: string[]) {
    setValue(name, values, { shouldDirty: true, shouldValidate: true });
  }

  function restoreAdvanced() {
    setValue('advanced', structuredClone(page.defaults.advanced), { shouldDirty: true, shouldValidate: true });
  }

  return (
    <>
      <Card title={t('sites.admission.gateway.title')} tip={t('sites.admission.gateway.tip')}>
        <FieldGrid>
          <SettingField control={control} name="gateway.host" label={t('sites.admission.gateway.host')} span={8} required placeholder="apim.exemplo.interno" />
          <SettingField control={control} name="gateway.port" label={t('sites.admission.gateway.port')} span={4} kind="number" required />
          <SettingField control={control} name="gateway.base_path" label={t('sites.admission.gateway.basePath')} span={12} tip={t('sites.admission.gateway.basePath.tip')} />
          <Controller
            control={control}
            name="gateway.trusted_ca_bundle"
            render={({ field, fieldState }) => (
              <Field
                label={t('sites.admission.gateway.ca')}
                span={12}
                tip={t('sites.admission.gateway.ca.tip')}
                error={fieldState.error?.message}
                help={
                  gatewayCA
                    ? tn('sites.admission.gateway.ca.loaded', (page.latest?.gateway_certificates ?? []).length, {
                        n: (page.latest?.gateway_certificates ?? []).length,
                      })
                    : t('sites.admission.gateway.ca.none')
                }
              >
                {(props) => <TextArea {...props} {...field} mono rows={3} placeholder="-----BEGIN CERTIFICATE-----" />}
              </Field>
            )}
          />
        </FieldGrid>
      </Card>

      <Card title={t('sites.admission.sts.title')} tip={t('sites.admission.sts.tip')}>
        <Controller
          control={control}
          name="sts.enabled"
          render={({ field }) => (
            <label className={s.toggle}>
              <input type="checkbox" checked={Boolean(field.value)} onChange={(event) => field.onChange(event.target.checked)} />
              {t('sites.admission.sts.enabled')}
            </label>
          )}
        />
        <FieldGrid>
          <SettingField control={control} name="sts.host" label={t('sites.admission.sts.host')} span={8} required={stsEnabled} disabled={!stsEnabled} />
          <SettingField control={control} name="sts.port" label={t('sites.admission.sts.port')} span={4} kind="number" required={stsEnabled} disabled={!stsEnabled} />
          <SettingField control={control} name="sts.path" label={t('sites.admission.sts.path')} span={8} required={stsEnabled} disabled={!stsEnabled} />
          <SettingField control={control} name="sts.timeout_ms" label={t('sites.admission.sts.timeout')} span={4} kind="number" disabled={!stsEnabled} />
          <SettingField control={control} name="sts.peer_spiffe_id" label={t('sites.admission.sts.peer')} span={12} required={stsEnabled} disabled={!stsEnabled} tip={t('sites.admission.sts.peer.tip')} />
        </FieldGrid>
      </Card>

      <Card title={t('sites.admission.signatures.title')} tip={t('sites.admission.signatures.tip')}>
        <Controller
          control={control}
          name="signatures.enforce"
          render={({ field }) => (
            <label className={s.toggle}>
              <input type="checkbox" checked={Boolean(field.value)} onChange={(event) => field.onChange(event.target.checked)} />
              {t('sites.admission.signatures.enforce')}
            </label>
          )}
        />
        <Controller
          control={control}
          name="signatures.image_globs"
          render={({ fieldState }) => (
            <Field label={t('sites.admission.signatures.globs')} span={12} required={enforce} error={fieldState.error?.message} tip={t('sites.admission.signatures.globs.tip')}>
              {(props) => (
                <div>
                  <ul className={s.list} aria-label={t('sites.admission.signatures.globs')}>
                    {globs.map((value, index) => (
                      <li key={`${value}-${index}`} className={s.listItem}>
                        <span className={s.mono}>{value}</span>
                        <IconButton icon="trash" label={t('sites.admission.signatures.globs.remove', { glob: value })} onClick={() => setList('signatures.image_globs', globs.filter((_, i) => i !== index))} />
                      </li>
                    ))}
                  </ul>
                  <div className={s.row}>
                    <TextInput {...props} mono value={glob} placeholder="registry.exemplo/agentes/*" onChange={(event) => setGlob(event.target.value)} />
                    <Button
                      variant="soft"
                      icon="plus"
                      disabled={!glob.trim()}
                      onClick={() => {
                        setList('signatures.image_globs', [...globs, glob.trim()]);
                        setGlob('');
                      }}
                    >
                      {t('sites.admission.signatures.globs.add')}
                    </Button>
                  </div>
                </div>
              )}
            </Field>
          )}
        />
        <Controller
          control={control}
          name="signatures.certificates"
          render={({ fieldState }) => (
            <Field label={t('sites.admission.signatures.certificates')} span={12} required={enforce} error={fieldState.error?.message} tip={t('sites.admission.signatures.certificates.tip')}>
              {(props) => (
                <div>
                  <ul className={s.list} aria-label={t('sites.admission.signatures.certificates')}>
                    {certificates.map((value, index) => {
                      // O resumo (titular e validade) vem do servidor para os certificados já salvos; um recém-colado
                      // aparece pelo início do texto até a próxima leitura.
                      const known = certificateList[index];
                      const label = known
                        ? t('sites.admission.signatures.certificate', { subject: known.subject || '—', until: dataCurta(known.not_after) ?? '—' })
                        : t('sites.admission.signatures.certificate.new', { n: index + 1 });
                      return (
                        <li key={`${index}-${value.length}`} className={s.listItem}>
                          <span>{label}</span>
                          <IconButton icon="trash" label={t('sites.admission.signatures.certificates.remove', { label })} onClick={() => setList('signatures.certificates', certificates.filter((_, i) => i !== index))} />
                        </li>
                      );
                    })}
                  </ul>
                  <TextArea {...props} mono rows={3} value={pem} placeholder="-----BEGIN CERTIFICATE-----" onChange={(event) => setPem(event.target.value)} />
                  <div className={s.row}>
                    <Button
                      variant="soft"
                      icon="plus"
                      disabled={!pem.trim()}
                      onClick={() => {
                        setList('signatures.certificates', [...certificates, pem.trim()]);
                        setPem('');
                      }}
                    >
                      {t('sites.admission.signatures.certificates.add')}
                    </Button>
                  </div>
                </div>
              )}
            </Field>
          )}
        />
      </Card>

      <Card
        title={
          <button type="button" className={s.advancedToggle} aria-expanded={advanced} onClick={() => setAdvanced((open) => !open)}>
            {advanced ? '▾ ' : '▸ '}
            {t('sites.admission.advanced.title')}
          </button>
        }
      >
        {advanced ? (
          <>
            <p className={s.note}>{t('sites.admission.advanced.note')}</p>
            {!page.defaults.advanced.helper.image ? (
              <Alert tone="warning" title={t('sites.admission.advanced.noRegistry.title')}>
                {t('sites.admission.advanced.noRegistry.message')}
              </Alert>
            ) : null}
            <FieldGrid>
              <SettingField control={control} name="advanced.helper.image" label={t('sites.admission.advanced.helper.image')} span={8} required />
              <SettingField control={control} name="advanced.helper.health_port" label={t('sites.admission.advanced.helper.healthPort')} span={4} kind="number" />
              <SettingField control={control} name="advanced.helper.workload_api_path" label={t('sites.admission.advanced.helper.workloadApiPath')} span={6} />
              <SettingField control={control} name="advanced.helper.socket" label={t('sites.admission.advanced.helper.socket')} span={6} />
              <SettingField control={control} name="advanced.helper.fs_group" label={t('sites.admission.advanced.helper.fsGroup')} span={4} kind="number" />
              <SettingField control={control} name="advanced.helper.certificate_mode" label={t('sites.admission.advanced.helper.certificateMode')} span={4} />
              <SettingField control={control} name="advanced.helper.key_mode" label={t('sites.admission.advanced.helper.keyMode')} span={4} />
              <SettingField control={control} name="advanced.proxy.image" label={t('sites.admission.advanced.proxy.image')} span={12} required />
              <SettingField control={control} name="advanced.proxy.listen_port" label={t('sites.admission.advanced.proxy.listenPort')} span={4} kind="number" />
              <SettingField control={control} name="advanced.proxy.health_port" label={t('sites.admission.advanced.proxy.healthPort')} span={4} kind="number" />
              <SettingField control={control} name="advanced.proxy.env_var_name" label={t('sites.admission.advanced.proxy.envVar')} span={4} />
              <SettingField control={control} name="advanced.proxy.connect_timeout" label={t('sites.admission.advanced.proxy.connectTimeout')} span={3} />
              <SettingField control={control} name="advanced.proxy.request_timeout" label={t('sites.admission.advanced.proxy.requestTimeout')} span={3} tip={t('sites.admission.advanced.proxy.timeout.tip')} />
              <SettingField control={control} name="advanced.proxy.stream_idle_timeout" label={t('sites.admission.advanced.proxy.streamIdleTimeout')} span={3} />
              <SettingField control={control} name="advanced.proxy.upstream_idle_timeout" label={t('sites.admission.advanced.proxy.upstreamIdleTimeout')} span={3} />
              <SettingField control={control} name="advanced.proxy.cpu_request" label={t('sites.admission.advanced.proxy.cpuRequest')} span={3} />
              <SettingField control={control} name="advanced.proxy.cpu_limit" label={t('sites.admission.advanced.proxy.cpuLimit')} span={3} />
              <SettingField control={control} name="advanced.proxy.memory_request" label={t('sites.admission.advanced.proxy.memoryRequest')} span={3} />
              <SettingField control={control} name="advanced.proxy.memory_limit" label={t('sites.admission.advanced.proxy.memoryLimit')} span={3} />
            </FieldGrid>
            <div className={s.row}>
              <Button variant="out" icon="refresh" onClick={restoreAdvanced}>
                {t('sites.admission.advanced.restore')}
              </Button>
            </div>
          </>
        ) : null}
      </Card>
    </>
  );
}
